/*
 * Maison Porte-Manteau : interactions du thème (01/11/2026).
 * Script classique, différé, sans dépendance. Il ne modifie pas le fonctionnement d'Horizon.
 *
 * Méga-menu (ordinateur) : Horizon ouvre un sous-menu en posant aria-expanded="true" sur <header-menu>
 * et en réglant --full-open-header-height / --submenu-height sur #header-component (assets/header-menu.js).
 * Le fondu et le léger glissement sont en CSS (assets/mpm-interactions.css). Ce script marque seulement
 * l'ouverture du menu fermé (attribut data-mpm-ouverture, retiré deux images plus tard) : le panneau prend
 * alors sa hauteur d'un coup et apparaît en fondu. D'un onglet à l'autre, sans marqueur, le panneau reste
 * ouvert et seule sa hauteur s'ajuste, en douceur.
 *
 * Le marqueur doit être posé avant le traitement d'Horizon, qui recalcule les styles pendant l'ouverture :
 * les écouteurs sont donc posés en capture sur window (Horizon écoute en capture sur document).
 */
(() => {
  'use strict';

  const OUVERTURE = 'data-mpm-ouverture';
  /* Une réouverture juste après une fermeture (le pointeur passe d'un onglet à l'autre par un espace vide)
     est traitée comme un changement d'onglet. */
  const DELAI_REOUVERTURE_MS = 220;

  let fermeLe = -Infinity;

  /** @param {EventTarget | null} cible */
  function enteteFerme(cible) {
    if (!(cible instanceof Element)) return null;
    const entete = cible.closest('#header-component');
    const menu = entete ? entete.querySelector('header-menu') : null;
    if (!entete || !menu || menu.getAttribute('aria-expanded') === 'true') return null;
    if (performance.now() - fermeLe < DELAI_REOUVERTURE_MS) return null;
    return entete;
  }

  /** @param {Element} entete */
  function marquerOuverture(entete) {
    entete.setAttribute(OUVERTURE, '');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => entete.removeAttribute(OUVERTURE));
    });
  }

  /* Survol d'un onglet du 1er niveau, menu fermé */
  window.addEventListener(
    'pointerenter',
    (event) => {
      const cible = event.target;
      if (!(cible instanceof Element) || !cible.classList.contains('menu-list__list-item')) return;
      const entete = enteteFerme(cible);
      if (entete) marquerOuverture(entete);
    },
    { capture: true }
  );

  /* Clavier ou clic sur le bouton d'ouverture d'un sous-menu, menu fermé */
  window.addEventListener(
    'click',
    (event) => {
      const cible = event.target;
      if (!(cible instanceof Element) || !cible.closest('.menu-list__disclosure, [slot="more"] .menu-list__link')) return;
      const entete = enteteFerme(cible);
      if (entete) marquerOuverture(entete);
    },
    { capture: true }
  );

  /* Fermeture : on note l'heure, et le marqueur ne doit pas survivre */
  /** @type {MutationObserver | null} */
  let observateur = null;

  function suivreFermeture() {
    if (observateur) observateur.disconnect();
    observateur = null;

    const entete = document.getElementById('header-component');
    if (!entete || typeof MutationObserver === 'undefined') return;

    observateur = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const menu = mutation.target;
        if (!(menu instanceof Element) || menu.localName !== 'header-menu') continue;
        if (mutation.oldValue === 'true' && menu.getAttribute('aria-expanded') !== 'true') {
          fermeLe = performance.now();
          entete.removeAttribute(OUVERTURE);
        }
      }
    });

    observateur.observe(entete, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded'],
      attributeOldValue: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', suivreFermeture, { once: true });
  } else {
    suivreFermeture();
  }

  /* Éditeur de thème : l'en-tête peut être rendu à nouveau. */
  document.addEventListener('shopify:section:load', suivreFermeture);
})();
